import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';
import { FormsModule } from '@angular/forms';

import { IconComponent, trackByFactory } from '@mode/capra';
import { FilterSearchPipe } from './filter-search.pipe';
import { TreeComponent } from './tree/tree.component';

interface TreeViewNodeContext<T> {
  $implicit: T;
}

@Component({
  standalone: true,
  selector: 'mode-tree-view',
  imports: [CommonModule, FilterSearchPipe, FormsModule, IconComponent, TreeComponent],
  templateUrl: './tree-view.component.html',
  styleUrls: ['./tree-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TreeViewComponent<T> implements OnChanges {
  @Input() nodes: T[] = [];
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore: TS2564
  @Input() template: TemplateRef<TreeViewNodeContext<T> | null>;
  @Input() indentation = 32;
  @Input() selectedId = '';
  @Input() showSearch = false;
  @Output() selectedIdChange = new EventEmitter<string>();
  private _nodesOpenState = this._getNodesOpenState(this.nodes);

  filterPattern = '';
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore: TS2564
  @Input() getId: (node: T) => string = () => '';
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore: TS2564
  @Input() getChildren: (node: T) => T[] = () => [];
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore: TS2564
  public getChildrenIfOpen: (node: T) => T[] = () => [];
  // eslint-disable-next-line @typescript-eslint/member-ordering
  public trackById = trackByFactory<T>(this.getId);

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['nodes']) {
      this._nodesOpenState = this._getNodesOpenState(changes['nodes'].currentValue);
    }
    if (changes?.['getChildren']) {
      this.getChildrenIfOpen = (node: T) => (this.isOpen(node) ? this.getChildren(node) : []);
    }
  }

  public onSelect(node: T): void {
    const nodeId = this.getId(node);
    if (this.getChildren(node).length) {
      this._toggleFolder(nodeId);
    } else {
      this.selectedIdChange.emit(nodeId);
    }
  }

  public isOpen(node: T): boolean {
    return this._nodesOpenState[this.getId(node)];
  }

  private _toggleFolder(nodeId: string): void {
    if (nodeId in this._nodesOpenState) {
      this._nodesOpenState[nodeId] = !this._nodesOpenState[nodeId];
    } else {
      this.selectedIdChange.emit(nodeId);
    }
  }

  private _getNodesOpenState(nodes: T[], state: Record<string, boolean> = {}): Record<string, boolean> {
    for (let node of nodes) {
      if (this.getChildren(node).length) {
        state[this.getId(node)] = false;
        this._getNodesOpenState(this.getChildren(node), state);
      }
    }
    return state;
  }
}
